/**
 * Absence is written, never blank, and it says which absence it is.
 *
 * This is the safety-critical piece of the v2 briefing and it needs no new
 * data. An empty cell beside *Slots* could mean "no slots required" or "we
 * have never recorded this", and those are opposite operational facts to plan
 * around. The old briefing renders `value || '—'` for both, which is the
 * ambiguity this file removes.
 *
 * Three outcomes:
 *
 * - `present`        the record holds something; render it as stored.
 * - `not-held`       a hole in our record; dashed, grey, with **Add this**
 *                    for anyone who can edit.
 * - `none-confirmed` a fact about the airport: the record explicitly says
 *                    there is nothing here.
 *
 * Two rules govern everything below.
 *
 * **Nothing is parsed out of prose.** A value is classified only by what the
 * *whole* value is. "No slots required for based aircraft" is a sentence a
 * planner has to read, not a confirmed none, and it stays `present`. The
 * token tables are matched against the entire trimmed value and never
 * searched inside one. Deriving a claim from a substring produces a confident
 * wrong answer on exactly the records that are least tidy.
 *
 * **The ambiguous middle falls towards `not-held`.** "n/a" is written for
 * both "not applicable" (a fact) and "not available" (a hole); "unknown" and
 * "TBD" are holes somebody typed. Claiming less is the safe failure — a
 * planner told we do not hold something goes and looks it up, and a planner
 * told "none" does not.
 *
 * There is no confirmation *date* here. Per-section check dates do not exist
 * on the model (data-gaps G1) and `updated_at` is a different and weaker
 * claim, so the "None — confirmed <date>" treatment ships as its "None" half.
 * Deriving the date would state a verification that never happened.
 */

export type V2FieldPresence = 'present' | 'not-held' | 'none-confirmed';

/** A section is `empty` only when every field in it is a hole. */
export type V2SectionPresence = 'populated' | 'empty';

/**
 * Whole values that state a fact: there is nothing here, and somebody said so.
 *
 * Deliberately small and deliberately spelled out. "not applicable" is
 * unambiguous; its abbreviation "n/a" is not, and lives in the other table.
 */
export const V2_NONE_CONFIRMED_TOKENS: readonly string[] = [
  'none',
  'nil',
  'no',
  'not applicable',
  'not required',
  'none required',
  'not needed',
  'no requirement',
  'no requirements',
];

/**
 * Whole values that are a hole in the record wearing a costume.
 *
 * The dashes are here because the old briefing itself renders `value || '—'`;
 * an em dash that has found its way back into the column is a stand-in for
 * nothing, not a claim about the airport. `null` / `undefined` / `NaN` are a
 * serialisation accident somebody saved.
 */
export const V2_NOT_HELD_TOKENS: readonly string[] = [
  '-',
  '--',
  '---',
  '–',
  '—',
  '.',
  '..',
  '...',
  '?',
  'n/a',
  'n.a.',
  'na',
  'tbd',
  'tba',
  'unknown',
  'not known',
  'no info',
  'no information',
  'no data',
  'null',
  'undefined',
  'nan',
];

/**
 * Blank characters `String.prototype.trim()` does not remove. The zero-width
 * family arrives by paste out of a PDF or a Word document, which is how
 * airport records are actually written, and a field holding one looks filled
 * in to every check that does not name them.
 */
const EXTRA_BLANK = '\\u200b-\\u200d';
const TRIM_RE = new RegExp(`^[\\s${EXTRA_BLANK}]+|[\\s${EXTRA_BLANK}]+$`, 'g');
const COLLAPSE_RE = new RegExp(`[\\s${EXTRA_BLANK}]+`, 'g');

/** Trailing sentence punctuation, so "None." matches the token "none". */
const TRAILING_PUNCTUATION_RE = /[.!?]+$/;

function trimBlank(value: string): string {
  return value.replace(TRIM_RE, '');
}

/**
 * The two forms a value is looked up under: as written, and with trailing
 * sentence punctuation removed. Both are lower-cased and have their internal
 * whitespace collapsed, because "not  required" and "not\nrequired" are the
 * same answer typed by two different people.
 */
function lookupForms(value: string): readonly [string, string] {
  const base = trimBlank(value).replace(COLLAPSE_RE, ' ').toLowerCase();
  const stripped = trimBlank(base.replace(TRAILING_PUNCTUATION_RE, ''));
  return [base, stripped];
}

function tokenSet(tokens: readonly string[]): ReadonlySet<string> {
  return new Set(tokens.flatMap(token => lookupForms(token)).filter(Boolean));
}

const NONE_CONFIRMED = tokenSet(V2_NONE_CONFIRMED_TOKENS);
const NOT_HELD = tokenSet(V2_NOT_HELD_TOKENS);

/**
 * Classify one field value off the airport record.
 *
 * Accepts `unknown` on purpose: `AirportModel` types more than a dozen of
 * these columns as the literal `null` (they are `blank=True, default=""`
 * TextFields on the Django side and arrive as `''`), so anything that
 * narrowed the parameter would be lying about what actually comes down the
 * wire.
 */
export function classifyField(value: unknown): V2FieldPresence {
  if (value === null || value === undefined) { return 'not-held'; }

  // Both booleans are facts somebody stored. `false` on `entry_airport` is
  // rendered as "No" by the current briefing and stays a value here.
  if (typeof value === 'boolean') { return 'present'; }

  if (typeof value === 'number') {
    return Number.isNaN(value) ? 'not-held' : 'present';
  }

  if (Array.isArray(value)) {
    // `airport_additionals[]` and `airport_files[]`. A row whose own value is
    // blank is not a runway line, so an array of blanks is still a hole.
    return value.some(item => classifyField(item) !== 'not-held') ? 'present' : 'not-held';
  }

  if (typeof value === 'object') { return 'present'; }

  if (typeof value !== 'string') { return 'not-held'; }

  const [base, stripped] = lookupForms(value);

  if (!base) { return 'not-held'; }
  if (NOT_HELD.has(base) || (!!stripped && NOT_HELD.has(stripped))) { return 'not-held'; }

  // Punctuation and nothing else: "..." or "?" left in the box.
  if (!stripped) { return 'not-held'; }

  if (NONE_CONFIRMED.has(base) || NONE_CONFIRMED.has(stripped)) { return 'none-confirmed'; }

  return 'present';
}

/**
 * The text to put on screen, trimmed and otherwise untouched.
 *
 * A pure trim on purpose. The *treatment* comes from `classifyField`; this
 * function never rewrites a value into a different one, so the two can never
 * disagree about what the record holds. Internal line breaks survive — the
 * old briefing pipes these through `formatText` into `innerHTML` and the
 * paragraph shape is part of the record.
 */
export function displayText(value: unknown): string {
  if (typeof value === 'string') { return trimBlank(value); }
  if (typeof value === 'number') { return Number.isNaN(value) ? '' : String(value); }
  return '';
}

/**
 * A section with nothing in it gets its own state rather than five dashed
 * rows, because an empty row still looks like a record. A single confirmed
 * none counts as content: the reader has been told something.
 */
export function classifySection(presences: readonly V2FieldPresence[]): V2SectionPresence {
  return presences.some(presence => presence !== 'not-held') ? 'populated' : 'empty';
}
